import {cn} from '@tempmee/core'
import type {ReactNode} from 'react'
import {textFieldHelperClass} from '../text-field/TextFieldVariants'

/**
 * The embedded label that floats up once the field is focused or holds a value.
 *
 * Deliberately the same treatment `TextField` uses, so a labelled
 * `InputTextField` and a `TextField` cannot drift apart: 16/0.15 resting, 12/16
 * shrunk, pinned left and non-interactive.
 *
 * `isError` is accepted for call-site symmetry with the box and helper, but
 * the label does not change colour in the error state: Figma paints it
 * `text/secondary` resting and `text/primary` floated in every enabled state,
 * Error included — only the underline and the helper go `error/main`.
 */
export function FloatingLabel({
  htmlFor,
  shrink,
  disabled,
  children,
}: {
  htmlFor?: string
  shrink: boolean
  disabled?: boolean
  isError?: boolean
  children: ReactNode
}) {
  const color = disabled
    ? 'text-text-disabled'
    : shrink
      ? 'text-text-primary'
      : 'text-text-secondary'

  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        'pointer-events-none absolute left-0 origin-left tracking-[0.15px] transition-all duration-150 ease-out',
        shrink
          ? 'top-0 text-[12px] font-normal leading-4'
          : '-translate-y-1/2 top-1/2 text-base font-semibold leading-6',
        color,
      )}
    >
      {children}
    </label>
  )
}

/** The caption under the field, carrying a hint or the validation message. */
export function FieldHelperText({
  id,
  disabled,
  isError,
  children,
}: {
  id?: string
  disabled?: boolean
  isError?: boolean
  children: ReactNode
}) {
  return (
    <p
      id={id}
      className={cn(
        textFieldHelperClass,
        disabled ? 'text-text-disabled' : isError ? 'text-error' : 'text-text-secondary',
      )}
    >
      {children}
    </p>
  )
}

/**
 * Wraps a field's input so a floating label has somewhere to pin.
 *
 * Unlabelled, it renders its children untouched — no extra element, so a field
 * that does not use a label keeps exactly the markup it had before the prop
 * existed. That is what makes `label` safe to add to a shipped component.
 */
export function FieldBox({
  label,
  htmlFor,
  shrink,
  disabled,
  isError,
  children,
}: {
  label?: ReactNode
  htmlFor?: string
  shrink: boolean
  disabled?: boolean
  isError?: boolean
  children: ReactNode
}) {
  if (label == null) return <>{children}</>

  return (
    <span className="relative flex h-10 flex-1 flex-col justify-center">
      <FloatingLabel htmlFor={htmlFor} shrink={shrink} disabled={disabled} isError={isError}>
        {label}
      </FloatingLabel>
      {children}
    </span>
  )
}
